/* PTL → PTL Billing → Billing Files.
   Generate a billing file from the approved shipments of a month (overall, client-wise ZIP or one client), and
   download / re-download generated files. A download is rebuilt every time, so POD links are always fresh. The client
   file never shows courier cost or margin; the internal file (PTL_BILLING) adds them and the Proship amount check. */
import React, { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Alert, Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { useSnackbar } from 'notistack'
import PageTitle from '../../components/PageTitle'
import { apiConstants, getDownload } from '../../common/constants'
import { BILLING_BASE, useBillingFilesQuery, useClientRowsQuery, useGenerateFileMutation } from '../../redux/ptlBillingApiSlice'
import { canBill, downloadFile, errText, fmtDateTime, inr, monthLabel, recentMonths, safeMonth } from './billingUtils'

const SCOPES = [['OVERALL', 'Overall: one file, all clients'], ['CLIENT_WISE', 'Client-wise: one file per client (ZIP)'], ['SINGLE', 'Single client']]
const SCOPE_LABEL = { OVERALL: 'Overall', CLIENT_WISE: 'Client-wise', SINGLE: 'Single client' }

export default function BillingFiles() {
  const [params, setParams] = useSearchParams()
  const month = safeMonth(params.get('month'))
  const billing = canBill()
  const { enqueueSnackbar } = useSnackbar()
  const [scope, setScope] = useState('OVERALL')
  const [merchant, setMerchant] = useState('')
  const [busy, setBusy] = useState(null)
  const { data: files = [], isFetching, error } = useBillingFilesQuery(month)
  const { data: clients = [] } = useClientRowsQuery({ cycleMonth: month })
  const [generate, { isLoading: generating }] = useGenerateFileMutation()

  const ready = useMemo(() => clients.filter((c) => c.approved + c.billed > 0), [clients])
  const approvedShipments = ready.reduce((a, c) => a + c.approved + c.billed, 0)
  const notApproved = clients.reduce((a, c) => a + c.pending + c.reopened, 0)

  const doGenerate = async () => {
    const res = await generate({ cycleMonth: month, scope, merchantOid: scope === 'SINGLE' ? merchant : undefined })
    if (res.error) return enqueueSnackbar(errText(res.error), { variant: 'error' })
    enqueueSnackbar(`${res.data.fileName} generated with ${res.data.shipmentCount} shipments`, { variant: 'success' })
    download(res.data, false)
  }
  // the internal file has Proship's courier cost and margin: confirm before it is downloaded
  const [internalFile, setInternalFile] = useState(null)
  const download = async (f, internal) => {
    setBusy(`${f.id}-${internal}`)
    try {
      await downloadFile(getDownload, `${apiConstants.BASE_LOCAL}${BILLING_BASE}/files/${f.id}/download?internal=${internal}`,
        internal ? f.fileName.replace(/(\.xlsx|\.zip)$/, '_INTERNAL$1') : f.fileName)
    } catch (e) {
      enqueueSnackbar('Could not download the file.', { variant: 'error' })
    } finally {
      setBusy(null)
    }
  }

  return (
    <Box>
      <PageTitle>Billing Files</PageTitle>
      <Card elevation={0} sx={{ border: 1, borderColor: 'divider', mb: 2 }}>
        <CardContent>
          <Typography sx={{ fontWeight: 600 }}>Generate billing file</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 2 }}>
            Only approved shipments go in; they are marked billed. Amounts exclude GST and every row has its POD link. Charges are Excel formulas on the
            client's terms (Terms sheet); a shaded cell is a fixed approved value. Files are shared with clients by the team.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField select size='small' label='Service month' value={month} onChange={(e) => setParams({ month: e.target.value })} sx={{ minWidth: 180 }}>
              {recentMonths(12).map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
            </TextField>
            <TextField select size='small' label='File type' value={scope} onChange={(e) => setScope(e.target.value)} sx={{ minWidth: 280 }}>
              {SCOPES.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
            </TextField>
            {scope === 'SINGLE' && (
              <TextField select size='small' label='Client' value={merchant} onChange={(e) => setMerchant(e.target.value)} sx={{ minWidth: 240 }}>
                {ready.length === 0 && <MenuItem value='' disabled>No client with approved shipments</MenuItem>}
                {ready.map((c) => <MenuItem key={c.merchantOid} value={c.merchantOid}>{c.merchantName} ({c.approved + c.billed})</MenuItem>)}
              </TextField>
            )}
            <LoadingButton variant='contained' loading={generating} disabled={!billing || !approvedShipments || (scope === 'SINGLE' && !merchant)}
              onClick={doGenerate} sx={{ textTransform: 'none' }}>Generate file</LoadingButton>
          </Box>
          <Typography sx={{ fontSize: 13, mt: 1.5 }}>
            Ready to bill in {monthLabel(month)}: <b>{approvedShipments}</b> approved shipments across <b>{ready.length}</b> clients,
            {' '}{inr(ready.reduce((a, c) => a + Number(c.approvedTotalExclGst ?? 0), 0), 2)} (approved and already billed).
            {notApproved > 0 && <span style={{ color: '#B45309' }}> {notApproved} not approved yet: they move to the next month if not approved by the close day.</span>}
          </Typography>
          {!billing && <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }}>Generating files and the internal file need PTL_BILLING access.</Typography>}
        </CardContent>
      </Card>

      <Card elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
        <CardContent>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Generated files {isFetching && '(loading…)'}</Typography>
          {error && <Alert severity='error'>{errText(error)}</Alert>}
          {!error && files.length === 0 && !isFetching && <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>No files for {monthLabel(month)} yet.</Typography>}
          {files.length > 0 && (
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>File</TableCell><TableCell>Type</TableCell><TableCell align='right'>Shipments</TableCell><TableCell align='right'>Clients</TableCell>
                  <TableCell align='right'>Amount (excl. GST)</TableCell><TableCell>Generated by</TableCell><TableCell>Generated on</TableCell>
                  <TableCell>Client file</TableCell>{billing && <TableCell>Internal file (do not share)</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {files.map((f) => (
                  <TableRow key={f.id}>
                    <TableCell sx={{ fontWeight: 500 }}>{f.fileName}</TableCell>
                    <TableCell>{SCOPE_LABEL[f.scope] || f.scope}{f.merchantName ? `: ${f.merchantName}` : ''}</TableCell>
                    <TableCell align='right'>{f.shipmentCount}</TableCell><TableCell align='right'>{f.clientCount}</TableCell>
                    <TableCell align='right'>{inr(f.amount)}</TableCell><TableCell>{f.generatedBy}</TableCell><TableCell>{fmtDateTime(f.generatedAt)}</TableCell>
                    <TableCell>
                      <LoadingButton size='small' startIcon={<DownloadOutlinedIcon />} loading={busy === `${f.id}-false`} onClick={() => download(f, false)} sx={{ textTransform: 'none' }}>Download</LoadingButton>
                    </TableCell>
                    {billing && (
                      <TableCell>
                        <LoadingButton size='small' color='warning' startIcon={<LockOutlinedIcon />} loading={busy === `${f.id}-true`} onClick={() => setInternalFile(f)}
                          sx={{ textTransform: 'none' }}>Download</LoadingButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1.5 }}>
            Each download is rebuilt from the approved amounts with fresh POD links (links are valid for 7 days). Inside: shipment details, chargeable weight, rate per kg,
            freight, fuel surcharge, DPH, surcharges (war and any other, see the Terms sheet), FOV, docket, ODA, appointment, handling, green tax, other charges, ad hoc (type and remarks), total excl. GST, POD link;
            Terms, Summary (with an OK / MISMATCH check) and How to read sheets. Courier cost and margin are only in the internal file. Zone names stay as they were when
            the file was made. A new file of the same type and client is made only when something new is approved; otherwise download the existing one.
          </Typography>
        </CardContent>
      </Card>
      <Dialog open={!!internalFile} onClose={() => setInternalFile(null)} maxWidth='xs' fullWidth>
        <DialogTitle>Internal file: do not share with the client</DialogTitle>
        <DialogContent>
          <Alert severity='warning'>
            This file shows Proship's courier cost, margin and the system amount check. It is for the Prozo team only and must
            <b> not</b> be sent to the client. Send the client file to the client.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInternalFile(null)}>Cancel</Button>
          <Button variant='contained' color='warning' onClick={() => { const f = internalFile; setInternalFile(null); download(f, true) }}>I understand, download</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
